import { z } from 'zod';
import { ALLOCATION_KEYS, COST_CATEGORIES, MEMBER_ROLES, TENANT_ADDRESS_FORMS } from '../enums';

export const UpdateMeBody = z.object({ name: z.string().trim().min(1).max(100).optional() });
/** `acceptLegal`: checkbox "Ich akzeptiere die AGB und habe die Datenschutzerklärung gelesen" (needed unless already given). */
export const CreateAccountBody = z.object({ name: z.string().trim().min(1).max(120), letterheadName: z.string().max(120).optional(), letterheadAddress: z.string().max(300).optional(), acceptLegal: z.boolean().optional() });
export const UpdateAccountBody = z.object({
  name: z.string().trim().min(1).max(120).optional(),
  letterhead: z.object({ name: z.string().max(120).optional(), address: z.string().max(300).optional(), iban: z.string().max(34).optional(), bic: z.string().max(11).optional(), email: z.string().email().optional(), phone: z.string().max(40).optional() }).optional(),
  defaults: z.object({
    allocationKeys: z.partialRecord(z.enum(COST_CATEGORIES), z.enum(ALLOCATION_KEYS)).optional(),
    vacancyFictivePersons: z.number().min(0).max(5).optional(),
    prepaymentSuggestionBufferPct: z.number().min(0).max(20).optional(),
    reminderDays: z.array(z.number().int().min(1).max(365)).max(5).optional(),
    prepaymentBasis: z.enum(['target', 'actual']).optional(),
    dueDay: z.number().int().min(1).max(28).optional(),
    retentionYears: z.union([z.literal(6), z.literal(8), z.literal(10)]).optional(),
    tenantAddress: z.enum(TENANT_ADDRESS_FORMS).optional(),
    /** Kautionsabrechnung spätestens n Monate nach Mietende (docs/18 V5, Standard 6). */
    depositSettleMonths: z.number().int().min(1).max(12).optional(),
  }).optional(),
});
/**
 * Caretaker property scope (docs/11 "Hausmeister-Bereich"): a caretaker is always limited to a property list, so
 * invites and new members with role caretaker need at least one property. Other roles ignore `propertyIds`.
 */
const PROPERTY_IDS = z.array(z.string().min(1)).max(200);
const caretakerHasProperties = (b: { role?: string; propertyIds?: string[] }) => b.role !== 'caretaker' || (b.propertyIds?.length ?? 0) > 0;
const CARETAKER_NEEDS_PROPERTY = { message: 'Hausmeister brauchen mindestens ein Objekt', path: ['propertyIds'] };
export const AddMemberBody = z.object({ email: z.string().email(), role: z.enum(MEMBER_ROLES).exclude(['owner']), propertyIds: PROPERTY_IDS.optional() }).refine(caretakerHasProperties, CARETAKER_NEEDS_PROPERTY);
/** `propertyIds` replaces the list; the server checks that a member who is (or becomes) caretaker keeps at least one property. */
export const UpdateMemberBody = z.object({ role: z.enum(MEMBER_ROLES).exclude(['owner']).optional(), propertyIds: PROPERTY_IDS.optional() });
export const CreateInviteBody = z.object({
  role: z.enum(['editor', 'caretaker', 'readonly', 'tenant']),
  email: z.string().email().optional(),
  tenancyId: z.string().optional(),
  personId: z.string().optional(),
  /** caretaker only (required there): the properties the new member is limited to. */
  propertyIds: PROPERTY_IDS.optional(),
  send: z.boolean().default(true),
}).refine(caretakerHasProperties, CARETAKER_NEEDS_PROPERTY);
/**
 * `appleAuthorizationCode`: fresh code from the Sign in with Apple sheet (iOS, only for Apple logins). The server
 * exchanges it and revokes the app's authorization at Apple (App Store 5.1.1(v)).
 */
const appleAuthorizationCode = z.string().min(10).max(2000).optional();
export const DeleteAccountBody = z.object({ confirmName: z.string().min(1), appleAuthorizationCode });
/** DELETE /me: deletes the login (tenant, team member, owner without own account). */
export const DeleteMeBody = z.object({ appleAuthorizationCode }).default({});
